import React from 'react';
import { CalendarPlus, Loader2, AlertTriangle, ArrowRight, Undo2 } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Field, Input, Select } from '../ui/Field';
import { pillPrimary, pillGhost } from '../ui/pills';
import { BesoinPrestatairesField, type BesoinLigne } from './BesoinPrestatairesField';

interface Calendrier { id: string; month: number; year: number; status?: string | null }

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const libelleCalendrier = (c: Calendrier) => `${MOIS[Math.max(0, Math.min(11, (c.month || 1) - 1))]} ${c.year}`;

/**
 * LE GESTE QUE TOUT LE PARCOURS PRÉPARE : la privatisation entre enfin dans Événementiel.
 *
 * Panneau inline (2026-09-01, refonte « assistant plein écran ») — remplace `CreerPrivatisationModal`, même
 * logique, plus de popup. Cette étape ne passe volontairement PAS par le panneau d'étape ordinaire
 * (`PipelineActionPanel`) : le serveur refuse d'atteindre « Privatisation créée » par une simple transition,
 * précisément parce qu'un événement sans date ni calendrier n'a pas sa place dans Événementiel.
 */
export function CreerPrivatisationPanel({ label, saving, error, backLabel, onConfirm, onBack, briefPrefill }: {
    label: string;
    saving: boolean;
    error: string;
    backLabel?: string | null;
    onConfirm: (payload: Record<string, any>) => void;
    onBack?: () => void;
    /** Valeurs reprises des réponses du Brief (question liée à un champ dans Paramètres > Brief) — demande
     *  d'Emeni (2026-09-02) : « nombre de personnes », « heure de début »... n'ont pas à être ressaisis s'ils
     *  ont déjà été notés pendant le brief. Toujours modifiable, à vérifier avant de confirmer. */
    briefPrefill?: { date?: string; start_time?: string; end_time?: string; num_people?: number } | null;
}) {
    const [calendriers, setCalendriers] = React.useState<Calendrier[] | null>(null);
    const [date, setDate] = React.useState(briefPrefill?.date || '');
    const [debut, setDebut] = React.useState(briefPrefill?.start_time || '19:00');
    const [fin, setFin] = React.useState(briefPrefill?.end_time || '23:00');
    const [couverts, setCouverts] = React.useState(briefPrefill?.num_people ? String(briefPrefill.num_people) : '');
    const [calendarId, setCalendarId] = React.useState('');
    const [choixManuel, setChoixManuel] = React.useState(false);
    // Le besoin en prestataires se saisit ICI, entre le devis signé et la création de la privatisation
    // (Emeni, 18/09) : c'est le moment où l'on a la soirée en tête. Il part avec la création, de sorte
    // que la privatisation existe déjà avec son besoin ; le booking se fait après, depuis Extras.
    const [besoins, setBesoins] = React.useState<BesoinLigne[]>([]);

    React.useEffect(() => {
        moduleApi.getEvenementielCalendars()
            .then((r: any) => setCalendriers(Array.isArray(r) ? r : []))
            .catch(() => setCalendriers([]));
    }, []);

    // Le calendrier suit la date tant que l'utilisateur ne l'a pas choisi lui-même : changer la date d'un
    // mois ne doit pas laisser un calendrier de l'ancien mois silencieusement sélectionné.
    const calendrierDuMois = React.useMemo(() => {
        if (!date || !calendriers) return null;
        const d = new Date(`${date}T00:00:00`);
        if (isNaN(d.getTime())) return null;
        return calendriers.find((c) => c.year === d.getFullYear() && c.month === d.getMonth() + 1) || null;
    }, [date, calendriers]);

    React.useEffect(() => {
        if (!choixManuel) setCalendarId(calendrierDuMois?.id || '');
    }, [calendrierDuMois, choixManuel]);

    const passeMinuit = !!(date && debut && fin && fin < debut);
    const moisSansCalendrier = !!date && calendriers !== null && !calendrierDuMois && !choixManuel;
    const bloque = !date || !debut || !fin || !calendarId || saving;

    // Même habillage que le panneau d'étape ordinaire (maquette 18a, « Ce qui bloque l'étape en cours ») :
    // bandeau « attention », puis les champs dans une carte blanche, puis Précédent / Créer.
    return (
        <div className="rounded-xl border border-state-warn/30 bg-state-warn-soft overflow-hidden">
            <div className="px-[15px] pt-3.5 pb-4 space-y-3.5">
                <div className="flex items-start gap-3">
                    <span className="w-7 h-7 shrink-0 rounded-lg bg-surface border border-state-warn/30 text-state-warn flex items-center justify-center"><CalendarPlus size={14} /></span>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[.09em] text-state-warn">Ce qui bloque l'étape en cours</div>
                        <h2 className="text-[13px] font-bold text-state-warn mt-0.5">Créer la privatisation</h2>
                        <p className="text-[12.5px] leading-[1.5] text-state-warn text-pretty mt-[3px]">
                            Le devis de <span className="font-semibold">{label}</span> est signé : l'événement peut maintenant
                            entrer dans Événementiel avec sa vraie date. C'est le seul moment où l'on est sûr qu'il aura
                            lieu — d'où le fait qu'il n'y figurait pas avant.
                        </p>
                    </div>
                </div>

                <div className="rounded-[10px] border border-border bg-surface p-3.5 space-y-3.5">
                    <Field label="Date de la privatisation" required help={briefPrefill?.date ? 'Reprise du brief — vérifiez avant de confirmer.' : undefined}>
                        {({ id }) => <Input id={id} type="date" value={date} onChange={(e) => setDate(e.target.value)} autoFocus />}
                    </Field>

                    <div className="grid grid-cols-2 gap-2.5">
                        <Field label="Début" required help={briefPrefill?.start_time ? 'Repris du brief.' : undefined}>
                            {({ id }) => <Input id={id} type="time" value={debut} onChange={(e) => setDebut(e.target.value)} />}
                        </Field>
                        <Field label="Fin" required help={briefPrefill?.end_time ? 'Reprise du brief.' : undefined}>
                            {({ id }) => <Input id={id} type="time" value={fin} onChange={(e) => setFin(e.target.value)} />}
                        </Field>
                    </div>

                    {passeMinuit && (
                        <p className="text-[12px] text-content-muted">La fin est antérieure au début : l'événement se terminera le lendemain matin.</p>
                    )}

                    <Field label="Nombre de couverts"
                        help={briefPrefill?.num_people ? 'Repris du brief — vérifiez avant de confirmer.' : "Laissez vide s'il n'est pas encore arrêté — il se complète dans la fiche de l'événement."}>
                        {({ id }) => <Input id={id} type="number" min={0} value={couverts} onChange={(e) => setCouverts(e.target.value)} />}
                    </Field>

                    <Field label="Calendrier" required help={calendrierDuMois && !choixManuel ? "Choisi automatiquement d'après la date." : undefined}>
                        {({ id }) => calendriers === null ? (
                            <div className="flex items-center gap-2 text-[12px] text-content-muted"><Loader2 size={12} className="animate-spin" /> Chargement des calendriers...</div>
                        ) : (
                            <Select id={id} value={calendarId} onChange={(e) => { setChoixManuel(true); setCalendarId(e.target.value); }}>
                                <option value="">—</option>
                                {calendriers.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {libelleCalendrier(c)}{c.status === 'ARCHIVED' ? ' (archivé)' : ''}
                                    </option>
                                ))}
                            </Select>
                        )}
                    </Field>

                    <BesoinPrestatairesField lignes={besoins} onChange={setBesoins} />

                    {moisSansCalendrier && (
                        <p className="text-[12px] text-state-warn font-semibold flex items-start gap-1.5">
                            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                            Aucun calendrier n'existe pour ce mois. Créez-le dans Événementiel, ou choisissez-en un
                            autre dans la liste ci-dessus.
                        </p>
                    )}
                </div>

                {error && <p className="text-[12px] text-state-danger font-semibold">{error}</p>}
            </div>

            <div className="border-t border-state-warn/30 bg-surface px-[15px] py-3 flex items-center justify-end gap-2 flex-wrap">
                {backLabel && onBack && (
                    <button type="button" onClick={onBack} disabled={saving} className={`${pillGhost(34)} mr-auto`}>
                        <Undo2 size={13} /> Précédent — {backLabel}
                    </button>
                )}
                <button type="button" className={pillPrimary(34)} disabled={bloque} onClick={() => onConfirm({
                    date, start_time: debut, end_time: fin, calendar_id: calendarId,
                    num_people: couverts.trim() ? Number(couverts) : null,
                    extras_needs: besoins.map((l) => ({ extra_type_id: l.extra_type_id, needed_count: l.needed_count })),
                })}>
                    {saving ? <Loader2 size={13} className="animate-spin" /> : null} Créer la privatisation <ArrowRight size={13} />
                </button>
            </div>
        </div>
    );
}
